import { useFetchRecordCount } from '@modules/common/hooks';
import { formatNumToStandardNotation } from '@modules/common/utils';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getHasMetadataFilterForEqlField } from '@modules/quantify/utils';
import { ChatRounded } from '@mui/icons-material';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useToggle } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { AggregatePeriod, TimeRange } from 'v2/core/entities';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  EmptyWhereClause,
  OperationType,
} from 'v2/core/services/queryBuilder/whereClause';

const trackQuantifyReportViewFeedbackEvents = createAnalyticsTracker(
  'quantifyReportViewFeedback',
);

export function QuantifyReportFeedbackMetric() {
  const query = useQuantifyStore(({ query }) => query);
  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause ?? EmptyWhereClause,
  );

  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );

  const measuredAsWhereClause =
    getHasMetadataFilterForEqlField(measuredAsCriteria);

  const metadataFilter = getHasMetadataFilterForEqlField(query.groupBy);

  const queryWhereClause = (() => {
    if (measuredAsWhereClause && metadataFilter) {
      return combineWhereClauses(
        measuredAsWhereClause,
        metadataFilter,
        OperationType.AND,
      );
    }
    return measuredAsWhereClause || metadataFilter;
  })();

  const queryContainer = {
    AggregatePeriod: AggregatePeriod.WEEK,
    DateRange: query.duration as TimeRange,
    WhereClause: queryWhereClause
      ? combineWhereClauses(whereClause, queryWhereClause, OperationType.AND)
      : whereClause,
  };

  const { data: count, isLoading } = useFetchRecordCount({
    queryContainer,
  });

  const handleViewFeedbackClick = () => {
    trackQuantifyReportViewFeedbackEvents({
      eventName: 'Quantify Report - View Feedback',
      trigger: 'Metric',
      query: {
        showMeLabel: query.groupBy?.displayName,
      },
    });

    toggleShouldOpenFeedbackDrawer();
  };

  return (
    <>
      <Box component="span">
        <Box
          component="span"
          sx={{
            mr: 0.5,
          }}
        >
          <Typography component="span" variant="subtitle2" color="text.primary">
            {isLoading ? (
              <Skeleton
                variant="rounded"
                width={14}
                height={14}
                sx={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  bgcolor: 'text.disabled',
                  mr: 0.2,
                  mt: -0.5,
                }}
              />
            ) : (
              formatNumToStandardNotation(count ?? 0)
            )}{' '}
          </Typography>
          <Typography
            component="span"
            variant="subtitle2"
            color="text.secondary"
          >
            feedback records match the filter
          </Typography>
          <Typography component="span" variant="subtitle2">
            :
          </Typography>
        </Box>
        <Button
          startIcon={<ChatRounded />}
          color="primary"
          size="small"
          onClick={handleViewFeedbackClick}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: 'max-content',
          }}
        >
          See All
        </Button>
      </Box>
      <QuantifyFeedbackListDrawer
        open={shouldOpenFeedbackDrawer}
        onClose={toggleShouldOpenFeedbackDrawer}
        queryContainer={queryContainer}
      />
    </>
  );
}
